import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Visibility from '@mui/icons-material/Visibility'
import { User } from '../interfaces/user'
import { viewAnnounce } from '../../services/viewAnnounce'
import { useTranslation } from 'react-i18next'
import Cookies from 'js-cookie'
import { handleFavoriteClick } from '../fonctions/addFavorite'
import GalleryCards from './GalleryCards'
import { MdOutlineStarOutline, MdOutlineStarPurple500 } from 'react-icons/md'
import { Libertin } from '../interfaces/libertin'
import { FaCheckCircle } from 'react-icons/fa'
import MyFixedTooltip from './MyFixedTooltip'
import {
  formatGender,
  formatMultipleStatuses,
  formatURL,
} from '../fonctions/formatGenderStatusUrl'

type GalleryItem = {
  id: number
  fileUrl: string
  fileName: string
  cover?: boolean
  status: number
}

type ProfileCardProps = {
  announce: Libertin
  user: User
  statusToShow: number[]
  gallery: GalleryItem[]
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  announce,
  user,
  statusToShow,
  gallery,
}) => {
  const { t } = useTranslation()
  const [isFavorite, setIsFavorite] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleClick = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    try {
      await viewAnnounce(Number(announce.id))
      window.location.href = `/announce/${formatGender(
        user.userProfile.gender,
      )}/${formatMultipleStatuses(announce.status)}/${formatURL(
        announce.title,
        Number(announce.id),
      )}`
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleIconMouseEnter = (event: React.MouseEvent) => {
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    })
  }

  const handleIconMouseLeave = () => {
    setTooltipPosition(null)
  }

  useEffect(() => {
    let favorites
    try {
      favorites = JSON.parse(Cookies.get('MyFavorites') || '{}')
    } catch (e) {
      favorites = {}
    }
    setIsFavorite(favorites[announce.id] !== undefined)
  }, [announce.id])

  const truncateUsername = (username: string, maxLength: number) => {
    return username.length > maxLength
      ? `${username.substring(0, maxLength)}...`
      : username
  }

  return (
    <Link
      to={`/announce/${formatGender(
        user.userProfile.gender,
      )}/${formatMultipleStatuses(announce.status)}/${formatURL(
        announce.title,
        Number(announce.id),
      )}`}
      className='group relative flex border border-1 border-grisclair hover:text-white hover:shadow-xl flex-col items-left bg-grisclair transition-all hover:bg-redpink rounded-lg w-full'
      style={{
        fontFamily: 'Poppins, sans-serif',
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='overflow-hidden rounded-t-lg h-[170px] sm:h-[200px] md:h-[220px] lg:h-[250px] xl:h-[270px]'>
        <GalleryCards
          gallery={gallery}
          coverImage={gallery.find(img => img.cover)}
          gender={user.userProfile?.gender}
          statusToShow={statusToShow}
          isHovered={isHovered}
        />
      </div>

      <div className='px-3 pt-2 lg:pt-1 xl:px-3'>
        <h3 className='text-sm sm:text-lg font-semibold truncate text-redpink group-hover:text-white'>
          {truncateUsername(user.nickname ?? '', 15)}
        </h3>

        {tooltipPosition && (
          <MyFixedTooltip
            text='verified-account'
            x={tooltipPosition.x}
            y={tooltipPosition.y - 10}
            className='transition-opacity bg-redpink p-20'
          />
        )}

        {user.address && (
          <p className='text-sm text-anthracite truncate'>
            {user.address.city}
          </p>
        )}
      </div>

      <div className='flex justify-between items-center px-3 py-2 mt-auto'>
        <div className='flex items-center gap-2'>
          {user.verifiedStatus === 'verified' && (
            <span
              className='text-emerald-500 bg-white rounded-full absolute bottom-20 right-1'
              onMouseEnter={handleIconMouseEnter}
              onMouseLeave={handleIconMouseLeave}
            >
              <FaCheckCircle className='text-xl sm:text-2xl' />
            </span>
          )}

          <button
            onClick={event =>
              handleFavoriteClick(event, announce.id, setIsFavorite)
            }
            className='flex items-center'
          >
            {isFavorite ? (
              <MdOutlineStarPurple500 className='transition-all text-xl sm:text-2xl text-yellow-500 group-hover:text-white' />
            ) : (
              <MdOutlineStarOutline className='text-xl sm:text-2xl' />
            )}
          </button>

          <div className='flex items-center gap-1'>
            <Visibility className='text-lg sm:text-xl' />
            <span className='text-sm'>{announce.views}</span>
          </div>
        </div>

        <div className='text-sm'>
          {announce.age} {t('years-old')}
        </div>
      </div>
    </Link>
  )
}

export default ProfileCard
